// Constants
import { TableName } from "@ms/uno-constants/lib/local/StoreConstants";
// Models
import { DynamicFilter } from "@ms/planner-models/lib/local/client/dynamicFilter/DynamicFilter";
import { EntityFilter, Filter } from "@ms/planner-models/lib/local/client/filter/Filter";
import { MruItem } from "@ms/planner-models/lib/local/client/mru/MruItem";
import { PinnedTask } from "@ms/planner-models/lib/local/client/pinnedTasks/PinnedTask";
import { Bucket } from "@ms/planner-models/lib/local/client/planner/bucket/Bucket";
import { ChatMessage } from "@ms/planner-models/lib/local/client/planner/chat/ChatMessage";
import { CustomFieldDefinition } from "@ms/planner-models/lib/local/client/planner/customFields/CustomFieldDefinition";
import { CustomView } from "@ms/planner-models/lib/local/client/planner/customView/CustomView";
import { Dependency } from "@ms/planner-models/lib/local/client/planner/dependency/Dependency";
import { Goal } from "@ms/planner-models/lib/local/client/planner/goal/Goal";
import { Sprint } from "@ms/planner-models/lib/local/client/planner/sprint/Sprint";
import { Plan } from "@ms/planner-models/lib/local/client/planner/plan/Plan";
import { Task } from "@ms/planner-models/lib/local/client/planner/task/Task";
import { TaskOrder } from "@ms/planner-models/lib/local/client/taskOrder/TaskOrder";
import { NonPersistedFilterFields } from "@ms/planner-models/lib/local/constants/FilterConstants";
import { User } from "@ms/planner-models/lib/local/client/user/User";
// Stores
import { STATUS_TABLES } from "@ms/planner-stores/lib/local/stores/IPersistedStoreTablesMap";
// Workers
import { StoreEntityStatusSerializationHandler } from "../../../../local/workers/cache/serialization/StoreEntityStatusSerializationHandler";
/**
 * Shared status serialization function for all status tables
 */ const serializeStatus = (entity, entityId)=>{
    return new StoreEntityStatusSerializationHandler().toSerializable(entityId, entity);
};
/**
 * Shared status deserialization function for all status tables
 */ const deserializeStatus = (serializedData)=>{
    return new StoreEntityStatusSerializationHandler().fromSerializable(serializedData.key, serializedData);
};
/**
 * Shared photo serialization function for all photo data tables (user and modern group photos).
 * Photos are stored as base64 data URI strings keyed by the entity id.
 */ const serializePhoto = (entity, entityId)=>({
        id: entityId,
        photo: entity
    });
/**
 * Shared photo deserialization function for all photo data tables (user and modern group photos).
 */ const deserializePhoto = (serializedData)=>serializedData.photo;
/**
 * Unified serialization map for both entity and status tables
 * Handles all table types based on IPersistedTableContentMap and IPersistedStoreTablesMap
 */ const SERIALIZATION_MAP = {
    // Entity tables
    [TableName.Mru]: (entity)=>entity.forPersistence(),
    [TableName.Plan]: (entity)=>entity.forSerialization(),
    [TableName.Bucket]: (entity)=>entity.forSerialization(),
    [TableName.Tasks]: (entity)=>entity.forSerialization(),
    [TableName.ViewConfig]: (entity, entityId)=>serializeViewConfig(entity, entityId),
    [TableName.CustomFieldDefinitions]: (entity)=>entity.forSerialization(),
    [TableName.CustomViews]: (entity)=>entity.forSerialization(),
    [TableName.PinnedTasks]: (entity)=>entity.forSerialization(),
    [TableName.TaskOrder]: (entity)=>entity.forSerialization(),
    [TableName.ChatMessage]: (entity)=>entity.forSerialization(),
    [TableName.DynamicFilter]: (entity)=>entity.forSerialization(),
    [TableName.Dependency]: (entity)=>entity.forSerialization(),
    [TableName.Goal]: (entity)=>entity.forSerialization(),
    [TableName.Sprints]: (entity)=>entity.forSerialization(),
    [TableName.PlannerDefaultPlanId]: (entity)=>entity,
    [TableName.UserPhotos]: serializePhoto,
    [TableName.ModernGroupPhotos]: serializePhoto,
    [TableName.Users]: (entity)=>entity.forSerialization(),
    // Status tables (generated from array)
    ...Object.fromEntries(STATUS_TABLES.map((tableName)=>[
            tableName,
            serializeStatus
        ]))
};
/**
 * Unified deserialization map for both entity and status tables
 * Handles all table types based on IPersistedTableContentMap and IPersistedStoreTablesMap
 */ const DESERIALIZATION_MAP = {
    // Entity tables
    [TableName.Mru]: (serializedData)=>{
        return MruItem.fromResource(serializedData);
    },
    [TableName.Plan]: (serializedData)=>{
        return Plan.fromPersistedResource(serializedData);
    },
    [TableName.Bucket]: (serializedData)=>{
        return Bucket.fromResource(serializedData);
    },
    [TableName.Tasks]: (serializedData)=>{
        return Task.fromPersistedResource(serializedData);
    },
    [TableName.ViewConfig]: deserializeViewConfig,
    [TableName.CustomFieldDefinitions]: (serializedData)=>{
        return CustomFieldDefinition.fromResource(serializedData);
    },
    [TableName.CustomViews]: (serializedData)=>{
        return CustomView.fromResource(serializedData);
    },
    [TableName.PinnedTasks]: (serializedData)=>{
        return PinnedTask.fromPersistedResource(serializedData);
    },
    [TableName.TaskOrder]: (serializedData)=>{
        return TaskOrder.fromPersistedResource(serializedData);
    },
    [TableName.ChatMessage]: (serializedData)=>{
        return ChatMessage.fromResource(serializedData);
    },
    [TableName.DynamicFilter]: (serializedData)=>{
        return DynamicFilter.fromPersistedResource(serializedData);
    },
    [TableName.Dependency]: (serializedData)=>{
        return Dependency.fromPersistedResource(serializedData);
    },
    [TableName.Goal]: (serializedData)=>{
        return Goal.fromPersistedResource(serializedData);
    },
    [TableName.Sprints]: (serializedData)=>{
        return Sprint.fromResource(serializedData);
    },
    [TableName.PlannerDefaultPlanId]: (serializedData)=>serializedData,
    [TableName.UserPhotos]: deserializePhoto,
    [TableName.ModernGroupPhotos]: deserializePhoto,
    [TableName.Users]: (serializedData)=>User.fromPersistedResource(serializedData),
    // Status tables (generated from array)
    ...Object.fromEntries(STATUS_TABLES.map((tableName)=>[
            tableName,
            deserializeStatus
        ]))
};
/**
 * Serializes content based on table name and store name using IPersistedTableContentMap and IPersistedStoreTablesMap
 * Uses a unified serialization map for both entity and status tables
 * @param tableName The name of the table
 * @param entity The entity to serialize
 * @param entityId The entity ID (required for status tables)
 * @returns The serialized entity
 */ export function serialize(tableName, entity, entityId) {
    const serializer = SERIALIZATION_MAP[tableName];
    if (serializer) {
        if (STATUS_TABLES.includes(tableName)) {
            if (!entityId) {
                throw new Error(`Entity ID is required for status table: ${tableName}`);
            }
            return serializer(entity, entityId);
        }
        return serializer(entity, entityId);
    }
    throw new Error(`Unknown serialization method for store: table: ${tableName}`);
}
/**
 * Deserializes content based on table name and store name using IPersistedTableContentMap and IPersistedStoreTablesMap
 * Uses a unified deserialization map for both entity and status tables
 * @param tableName The name of the table
 * @param serializedData The serialized data to deserialize
 * @returns The deserialized entity
 */ export function deserialize(tableName, serializedData) {
    const deserializer = DESERIALIZATION_MAP[tableName];
    if (deserializer) {
        return deserializer(serializedData);
    }
    throw new Error(`Unknown deserialization method for table: ${tableName}`);
}
/**
 * Serializes a view config into a persisted resource
 * @param config The view config to serialize
 * @param viewId The view ID string
 * @returns The serialized view config persisted resource
 */ export function serializeViewConfig(config, viewId) {
    const strippedFilters = config.taskFilter ? config.taskFilter.filters.filter((f)=>!NonPersistedFilterFields.has(f.field)) : null;
    const persistableFilter = strippedFilters && strippedFilters.length > 0 ? new EntityFilter(strippedFilters) : null;
    return {
        viewId,
        taskFilter: JSON.stringify(persistableFilter),
        taskGrouping: config.taskGrouping,
        sortCriteria: config.sortCriteria ? config.sortCriteria : null,
        gridColumnWidths: config.gridColumnWidths,
        ganttZoomScaleByViewType: config.ganttZoomScaleByViewType,
        isUnscheduledColumnVisible: config.isUnscheduledColumnVisible
    };
}
/**
 * Deserializes a view config persisted resource into a view config
 * @param resource The persisted resource to deserialize
 * @returns The deserialized view config
 */ export function deserializeViewConfig(resource) {
    const filterString = resource.taskFilter ?? "";
    let filterObject = null;
    try {
        const parsedFilter = JSON.parse(filterString);
        filterObject = new EntityFilter(parsedFilter.filters.map((filter)=>new Filter(filter.field, filter.values, filter.label, typeof filter.active === "boolean" ? filter.active : true)));
    } catch  {
        filterObject = null;
    }
    return {
        taskFilter: filterObject,
        taskGrouping: resource.taskGrouping,
        sortCriteria: resource.sortCriteria,
        gridColumnWidths: resource.gridColumnWidths,
        ganttZoomScaleByViewType: resource.ganttZoomScaleByViewType ?? null,
        isUnscheduledColumnVisible: resource.isUnscheduledColumnVisible ?? null
    };
}
